/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */

import { type Signal } from '@preact/signals-core';
import { AsyncDirective } from 'lit/async-directive.js';
import { directive } from 'lit/directive.js';

class WatchDirective extends AsyncDirective {
  private __signal?: Signal;
  private __dispose?: () => void;
  // ✅ 标记是否已断开连接，防止断开后的回调执行
  private __isDisconnected = false;

  override render(signal: Signal<unknown>) {
    if (signal !== this.__signal) {
      this.__dispose?.();
      this.__signal = signal;
      this.__isDisconnected = false;

      // Whether the subscribe() callback is called because of this render
      // pass, or because of a separate signal update.
      let updateFromLit = true;
      this.__dispose = signal.subscribe(value => {
        // ✅ 如果已断开连接，不执行回调
        if (this.__isDisconnected) return;
        // The subscribe() callback is called synchronously during subscribe.
        // Ignore the first call since we return the value below in that case.
        if (updateFromLit === false) {
          this.setValue(value);
        }
      });
      updateFromLit = false;
    }

    // We use peek() so that the signal access is not tracked by the effect
    // created by SignalWatcher.performUpdate(). This means that a signal
    // update won't trigger a full element update if it's only passed to
    // watch() and not otherwise accessed by the element.
    return signal.peek();
  }

  protected override disconnected(): void {
    // ✅ 先标记断开连接，防止后续回调执行
    this.__isDisconnected = true;
    this.__dispose?.();
    // ✅ 清理引用，防止内存泄漏
    this.__dispose = undefined;
    this.__signal = undefined;
  }

  protected override reconnected(): void {
    // ✅ 重置断开连接标记
    this.__isDisconnected = false;
    // Since we disposed the subscription in disconnected() we need to
    // resubscribe here. We don't ignore the synchronous callback call because
    // the signal might have changed while the directive is disconnected.
    //
    // There are two possible reasons for a disconnect:
    //   1. The host element was disconnected.
    //   2. The directive was not rendered during a render
    // In the first case the element will not schedule an update on reconnect,
    // so we need the synchronous call here to set the current value.
    // In the second case, we're probably reconnecting *because* of a render,
    // so the synchronous call here will go before a render call, and we'll get
    // two sets of the value (setValue() here and the return in render()), but
    // this is ok because the value will be dirty-checked by lit-html.
    if (this.__signal) {
      this.__dispose = this.__signal.subscribe(value => {
        if (this.__isDisconnected) return;
        this.setValue(value);
      });
    }
  }
}

/**
 * Renders a signal and subscribes to it, updating the part when the signal
 * changes.
 */
export const watch = directive(WatchDirective);
